import React from "react";

/** One guided checkout. The cart page keeps quantities and offers; every step
 * from holding the items to paying is shown in order on the checkout page. */
export const checkoutStepActions = [
  "cart.save",
  "checkout.address.save",
  "checkout.context.save",
  "checkout.prepare",
  "checkout.freeze",
  "checkout.confirm",
  "checkout.fund",
] as const;
/** Actions the cart page keeps; the rest move to checkout. */
export const cartPageActions = ["cart.save", "growth.cart.consent", "growth.cart.withdraw"];

const words = {
  en: { title: "Checkout steps", steps: ["Hold your items", "Address", "Delivery", "Get your total", "Lock in the price", "Review payment", "Pay"], hold: "Hold your items", done: "Done", now: "Now", toCheckout: "Continue to checkout", empty: "Your cart is empty." },
  es: { title: "Pasos para pagar", steps: ["Reserva tus artículos", "Dirección", "Envío", "Obtén el total", "Fija el precio", "Revisa el pago", "Paga"], hold: "Reserva tus artículos", done: "Hecho", now: "Ahora", toCheckout: "Continuar al pago", empty: "Tu carrito está vacío." },
  fr: { title: "Étapes du paiement", steps: ["Réservez vos articles", "Adresse", "Livraison", "Obtenez le total", "Bloquez le prix", "Vérifiez le paiement", "Payez"], hold: "Réservez vos articles", done: "Fait", now: "Maintenant", toCheckout: "Passer au paiement", empty: "Votre panier est vide." },
  de: { title: "Schritte zur Kasse", steps: ["Artikel reservieren", "Adresse", "Versand", "Gesamtbetrag abrufen", "Preis festlegen", "Zahlung prüfen", "Bezahlen"], hold: "Artikel reservieren", done: "Erledigt", now: "Jetzt", toCheckout: "Weiter zur Kasse", empty: "Dein Warenkorb ist leer." },
  ja: { title: "購入の手順", steps: ["商品を確保", "住所", "配送", "合計を確認", "価格を確定", "支払いを確認", "支払う"], hold: "商品を確保", done: "完了", now: "現在", toCheckout: "購入手続きへ進む", empty: "カートは空です。" },
} as const;
export const checkoutWords = (locale: string) => words[locale as keyof typeof words] ?? words.en;

type Act = { id: string; label: string; resource?: { id: string }; instanceId?: string };
const stepOf = (id: string) => { const i = (checkoutStepActions as readonly string[]).indexOf(id); return i < 0 ? checkoutStepActions.length : i; };
/** Earlier forms stay available for edits (quantity, address), so the step
 * you are on is the furthest one the server currently offers. */
const currentStep = (ids: readonly string[]) => Math.max(-1, ...ids.filter(id => (checkoutStepActions as readonly string[]).includes(id)).map(stepOf));

/** Checkout page actions: the current step first, then later ones, then
 * earlier steps (to change quantities or addresses), then everything else. */
export function mergeCheckoutActions<A extends Act>(checkout: readonly A[], cart: readonly A[] | undefined, locale: string): A[] {
  const w = checkoutWords(locale);
  const fromCart = (cart ?? []).filter(a => (checkoutStepActions as readonly string[]).includes(a.id)
    && !checkout.some(b => b.id === a.id && b.resource?.id === a.resource?.id));
  const relabel = (a: A): A => a.id === "cart.save" ? { ...a, label: [w.hold, ...a.label.split(" — ").slice(1)].join(" — ") } : a;
  const all = [...fromCart, ...checkout], current = currentStep(all.map(a => a.id));
  const rank = (id: string) => { const s = stepOf(id); return s >= checkoutStepActions.length ? 3e3 : s >= current ? s : 1e3 + s; };
  return all.map(relabel).map((a, i) => ({ a, i })).sort((x, y) => rank(x.a.id) - rank(y.a.id) || x.i - y.i).map(x => x.a);
}

export function CheckoutSteps({ actions, finished, locale }: { actions: readonly Act[]; finished: boolean; locale: string }) {
  const w = checkoutWords(locale);
  const ids = actions.map(a => a.id).filter(id => (checkoutStepActions as readonly string[]).includes(id));
  if (!ids.length && !finished) return null;
  const current = finished ? w.steps.length : currentStep(ids);
  return (
    <nav className="checkout-steps" aria-label={w.title}>
      <ol>
        {w.steps.map((label, i) => (
          <li key={i} className={i < current ? "done" : i === current ? "current" : "upcoming"} aria-current={i === current ? "step" : undefined}>
            <span className="checkout-step-number" aria-hidden="true">{i < current ? "✓" : i + 1}</span>
            <span>{label}</span>
            {i < current && <span className="sr-only"> ({w.done})</span>}
            {i === current && <span className="sr-only"> ({w.now})</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}
